import React, { useState } from 'react';
import { useTheme } from '../ThemeContext';
import { isNativeApp } from '../utils/platform';
import { ASK_YOUR_SPONSOR_TEXT, nativeOwnAccountAllowed } from '../shared/nativeSignupCopy';
import { HOMEOWNER_BUTTON, HOMEOWNER_CONSENT_ERROR } from '../shared/consentCopy';
import { signupFieldErrors } from '../shared/homeownerSignup';
import { signupHomeowner } from '../utils/homeownerApi';
import { CountyField } from './ServiceAreaFields';

function inputStyle(colors) {
  return {
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: '0.75rem',
    padding: '0.85rem',
    borderRadius: '8px',
    border: `2px solid ${colors.borderLight || colors.border}`,
    background: colors.surface,
    color: colors.text,
    fontSize: '1rem',
    minHeight: '48px',
  };
}

export default function HomeownerSignup({ native, onSignedUp, onSignIn }) {
  const { colors, isDark } = useTheme();
  const onDevice = native === undefined ? isNativeApp() : native === true;
  const allowOwn = nativeOwnAccountAllowed(onDevice);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [countyFips, setCountyFips] = useState('');
  const [countySource, setCountySource] = useState('');
  const [sponsorCode, setSponsorCode] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  if (!allowOwn) {
    return (
      <div data-testid="homeowner-signup" style={{ background: colors.background, color: colors.text, minHeight: '100vh', padding: '1.5rem', boxSizing: 'border-box' }}>
        <p data-testid="native-ask-sponsor" style={{ margin: 0, fontSize: '1.05rem', lineHeight: 1.55, textAlign: 'center' }}>
          {ASK_YOUR_SPONSOR_TEXT}
        </p>
      </div>
    );
  }

  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      name,
      email,
      password,
      address,
      city,
      state,
      zip,
      countyFips,
      countySource,
      sponsorCode,
      accepted,
    };
    const errors = signupFieldErrors(payload);
    if (errors.length) {
      setError(errors[0] || HOMEOWNER_CONSENT_ERROR);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await signupHomeowner(payload);
      if (onSignedUp) onSignedUp(result);
    } catch (signupError) {
      setError((signupError && signupError.message) || 'Could not create the account.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="homeowner-signup" style={{ background: colors.background, color: colors.text, minHeight: '100vh', padding: '1.25rem' }}>
      <form onSubmit={submit} style={{ maxWidth: '560px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '1.6rem', marginBottom: '0.35rem' }}>Create your account</h1>
        <p style={{ color: colors.textSecondary, marginTop: 0 }}>
          Homeowners: add your home, then answer a few questions about it.
        </p>
        <input aria-label="Name" placeholder="Name" autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} style={inputStyle(colors)} required />
        <input aria-label="Email" type="email" placeholder="Email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} style={inputStyle(colors)} required />
        <input aria-label="Password" type="password" placeholder="Password (min 8 characters)" autoComplete="new-password" minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} style={inputStyle(colors)} required />
        <input aria-label="Home address" placeholder="Home address" autoComplete="street-address" value={address} onChange={(event) => setAddress(event.target.value)} style={inputStyle(colors)} required />
        <input aria-label="City" placeholder="City" autoComplete="address-level2" value={city} onChange={(event) => setCity(event.target.value)} style={inputStyle(colors)} required />
        <input aria-label="State" placeholder="State" autoComplete="address-level1" value={state} onChange={(event) => setState(event.target.value)} style={inputStyle(colors)} required />
        <input aria-label="ZIP code" placeholder="ZIP code" inputMode="numeric" autoComplete="postal-code" value={zip} onChange={(event) => setZip(event.target.value)} style={inputStyle(colors)} required />
        <CountyField
          zip={zip}
          state={state}
          fips={countyFips}
          source={countySource}
          colors={colors}
          onChange={(county) => {
            setCountyFips((county && county.property_county_fips) || '');
            setCountySource((county && county.property_county_source) || '');
          }}
        />
        <input
          aria-label="Sponsor code"
          placeholder="Sponsor code (optional)"
          value={sponsorCode}
          onChange={(event) => setSponsorCode(event.target.value)}
          style={inputStyle(colors)}
          maxLength={6}
        />
        <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', margin: '0.25rem 0 1rem' }}>
          <input
            type="checkbox"
            checked={accepted}
            aria-label="Agree to the Terms of Service and Privacy Policy"
            onChange={(event) => {
              setAccepted(event.target.checked);
              if (event.target.checked) setError('');
            }}
            style={{ marginTop: '0.2rem', width: '1.1rem', height: '1.1rem', flexShrink: 0 }}
          />
          <span>
            I am 18 or older and agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#0f766e', fontWeight: 650 }}>Terms of Service</a>
            {' '}
            (including the <strong>arbitration agreement and class action waiver</strong>, which I may opt out of within 30 days), and I have read the{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#0f766e', fontWeight: 650 }}>Privacy Policy</a>.
          </span>
        </label>
        {error ? <p role="alert" style={{ color: isDark ? '#fecaca' : '#991b1b' }}>{error}</p> : null}
        <button
          type="submit"
          disabled={saving || !accepted}
          style={{
            width: '100%',
            minHeight: '48px',
            border: 'none',
            borderRadius: '8px',
            background: '#59c4c4',
            color: '#083838',
            fontWeight: 750,
            fontSize: '1rem',
            cursor: saving || !accepted ? 'not-allowed' : 'pointer',
            opacity: saving || !accepted ? 0.6 : 1,
          }}
        >
          {saving ? 'Creating account...' : HOMEOWNER_BUTTON}
        </button>
        <p style={{ textAlign: 'center', color: colors.textSecondary }}>
          Already have an account?{' '}
          <button type="button" onClick={onSignIn} style={{ background: 'none', border: 'none', color: '#0f766e', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' }}>
            Sign in
          </button>
        </p>
      </form>
    </div>
  );
}
